import axiosClient from "@/shared/lib/axios/axios.ts";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@/shared/types/api.ts";
import { API_ROUTES } from "@/machines/api/config.ts";
import { Machine, MachineSettingsInput } from "@/machines/types/machine.ts";
import { MachineStats } from "@/machines/types/machine-stats.ts";
import { formatDateTime } from "@/shared/utils/date/format-datetime.ts";

// API response type (before transformation)
interface MachineApiResponse {
  id: number;
  name: string;
  status: "offline" | "online";
  device_mxid: string;
  last_seen: string | null;
  location: Machine["location"];
  last_gps: Machine["last_gps"];
  color?: string;
  make?: string;
  model?: string;
  bucket_width_mm?: number;
  loading_time_target_s?: number;
  waiting_time_target_s?: number;
  buckets_per_truck_target?: number;
  is_active?: boolean;
}

/**
 * Transform API response to Machine type with parsed dates
 */
const transformMachine = (apiMachine: MachineApiResponse): Machine => {
  return {
    ...apiMachine,
    last_seen: apiMachine.last_seen ? new Date(apiMachine.last_seen) : null,
  };
};

export interface IGetMachinesInput {
  location_id: number;
}

export type IGetMachinesResponse = Machine

export const getMachinesByLocation = async (locationId?: string | number) => {
  return axiosClient
    .get<IGetMachinesInput, AxiosResponse<ApiResponse<MachineApiResponse[]>>>(
      `${API_ROUTES.MACHINES}?location_id=${locationId}`
    )
    .then((res) => ({
      ...res.data,
      data: res.data.data.map(transformMachine),
    }));
};

export interface IGetMachineByIdInput {
  id: number;
  location_id: number;
}

export type IGetMachineByIdResponse = Machine

export const getMachineById = async (id: number) => {
  return axiosClient
    .get<IGetMachineByIdInput, AxiosResponse<ApiResponse<MachineApiResponse>>>(
      `${API_ROUTES.MACHINES}${id}`
    )
    .then((res) => ({
      ...res.data,
      data: transformMachine(res.data.data),
    }));
};


export interface IGetKpiStatsInput {
  device_mxid?: Machine["device_mxid"];
  start_time: Date;
  end_time: Date;
}

export interface IGetKpiStatsResponse {
   current: MachineStats;
   previous: MachineStats;
}


export const getKpiStats = async (params: IGetKpiStatsInput): Promise<ApiResponse<IGetKpiStatsResponse>> => {
  return axiosClient.get<IGetKpiStatsInput, AxiosResponse<ApiResponse<IGetKpiStatsResponse>>>(API_ROUTES.KPI_STATS, {
    params: {
      device_mxid: params.device_mxid,
      start_time: formatDateTime(params.start_time, "start"),
      end_time: formatDateTime(params.end_time, "end")
    }
  }).then((res) => {
    return res.data;
  });
}

export interface IUpdateMachineSettingsInput {
  id: number;
  settings: MachineSettingsInput;
}

export const updateMachineSettings = async ({ id, settings }: IUpdateMachineSettingsInput) => {
  return axiosClient
    .patch<MachineSettingsInput, AxiosResponse<ApiResponse<MachineApiResponse>>>(
      `${API_ROUTES.MACHINES}${id}`,
      settings
    )
    .then((res) => ({
      ...res.data,
      data: transformMachine(res.data.data),
    }));
};